/**
 * Budget domain (`budget` table) — AndroMoney parity.
 *
 * Business rules:
 *   - A budget can be global (`category_id` NULL → all spending) or
 *     by category (including spending from direct subcategories).
 *   - Spending is ALWAYS measured in `base_amount` (the user's base currency)
 *     and only `kind = 'expense'` counts (transfer fees are
 *     considered operating cost, not budgetable spending).
 *   - `month` period: accounting month honoring `user.month_start_day`
 *     (see `accountingMonthRange`). `year` period: calendar year (Jan 1–Jan 1).
 *   - Rollover (see `computeBudgetProgress`): the effective limit carries over
 *     the surplus (or overspend, as a negative value) ONLY from the immediately
 *     preceding period — it does not accumulate infinitely backward.
 */
import {
  aliasedTable,
  asc,
  budget,
  category,
  eq,
  type PowerSyncDrizzleDB,
  sql,
  tx,
  user,
} from "@cashsync/powersync";
import { accountingMonthRange, type PeriodRange, shiftAccountingMonth } from "./period";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

export type BudgetPeriod = "month" | "year";

export interface BudgetRow {
  id: string;
  category_id: string | null;
  /** Minor units (cents). */
  amount: number;
  period: BudgetPeriod;
  start_on: string | null;
  rollover: boolean;
}

export interface BudgetDraft {
  /** NULL = global budget (all categories). */
  categoryId?: string | null;
  amount: number;
  period: BudgetPeriod;
  startOn?: string | null;
  rollover?: boolean;
}

/** Progress for a budget within an accounting period. */
export interface BudgetProgress {
  budgetId: string;
  categoryId: string | null;
  period: BudgetPeriod;
  rollover: boolean;
  /** Amount configured for the period (minor units). */
  baseAmount: number;
  /** Carryover from the previous period (0 without rollover; may be negative). */
  carryover: number;
  /** Effective period limit = baseAmount + carryover. */
  budgeted: number;
  spent: number;
  remaining: number;
  /** spent / budgeted, clamped to [0, BUDGET_RATIO_CAP]. */
  ratio: number;
}

/** User budgets (budgets have no logical deletion). */
export function getBudgetsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(budget).where(eq(budget.user_id, userId)).orderBy(asc(budget.created_at));
}

// ─── Period ranges ────────────────────────────────────────────────────────────

export interface BudgetPeriodRanges {
  month: { current: PeriodRange; previous: PeriodRange };
  year: { current: PeriodRange; previous: PeriodRange };
}

/**
 * Current and previous ranges for monthly budgets (accounting month with
 * `monthStartDay`) and yearly budgets (calendar year — AndroMoney does not shift the year).
 */
export function budgetPeriodRanges(today: Date, monthStartDay = 1): BudgetPeriodRanges {
  const monthCurrent = accountingMonthRange(today, monthStartDay);
  const monthPrevious = shiftAccountingMonth(monthCurrent, -1, monthStartDay);
  const y = today.getFullYear();
  const yearCurrent: PeriodRange = {
    fromISO: new Date(y, 0, 1).toISOString(),
    toISO: new Date(y + 1, 0, 1).toISOString(),
  };
  const yearPrevious: PeriodRange = {
    fromISO: new Date(y - 1, 0, 1).toISOString(),
    toISO: yearCurrent.fromISO,
  };
  return {
    month: { current: monthCurrent, previous: monthPrevious },
    year: { current: yearCurrent, previous: yearPrevious },
  };
}

// ─── Spending by budget (observable aggregate query) ──────────────────────────

export interface BudgetSpendRow {
  id: string;
  category_id: string | null;
  amount: number | null;
  period: string | null;
  rollover: number | null;
  /** Spending in the current period in base_amount (minor units). */
  spent_current: number;
  /** Spending in the immediately preceding period (for rollover). */
  spent_previous: number;
}

const txCategory = aliasedTable(category, "budget_tx_category");

/** SQL condition: the tx falls in the range for the budget's period. */
function budgetPeriodInRange(month: PeriodRange, year: PeriodRange) {
  return sql`(CASE WHEN ${budget.period} = 'year'
      THEN (${tx.occurred_at} >= ${year.fromISO} AND ${tx.occurred_at} < ${year.toISO})
      ELSE (${tx.occurred_at} >= ${month.fromISO} AND ${tx.occurred_at} < ${month.toISO}) END)`;
}

/**
 * One row per budget with aggregate spending for the current and previous
 * periods. Category matching includes direct subcategories (2-level tree):
 * `tx.category_id = budget.category_id OR parent(tx.category_id) =
 * budget.category_id`. A global budget (`category_id` NULL) sums everything.
 */
export function getBudgetSpendQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  ranges: BudgetPeriodRanges,
) {
  const categoryMatch = sql`(${budget.category_id} IS NULL OR ${tx.category_id} = ${budget.category_id} OR ${txCategory.parent_id} = ${budget.category_id})`;

  return db
    .select({
      id: budget.id,
      category_id: budget.category_id,
      amount: budget.amount,
      period: budget.period,
      rollover: budget.rollover,
      spent_current: sql<number>`COALESCE(SUM(CASE
        WHEN ${tx.id} IS NOT NULL AND ${categoryMatch} AND ${budgetPeriodInRange(ranges.month.current, ranges.year.current)}
        THEN ${tx.base_amount} ELSE 0 END), 0)`.as("spent_current"),
      spent_previous: sql<number>`COALESCE(SUM(CASE
        WHEN ${tx.id} IS NOT NULL AND ${categoryMatch} AND ${budgetPeriodInRange(ranges.month.previous, ranges.year.previous)}
        THEN ${tx.base_amount} ELSE 0 END), 0)`.as("spent_previous"),
    })
    .from(budget)
    .leftJoin(
      tx,
      sql`${tx.user_id} = ${budget.user_id} AND ${tx.deleted_at} IS NULL AND ${tx.kind} = 'expense'`,
    )
    .leftJoin(txCategory, eq(tx.category_id, txCategory.id))
    .where(eq(budget.user_id, userId))
    .groupBy(budget.id)
    .orderBy(asc(budget.created_at));
}

// ─── Progress calculation (pure, testable) ────────────────────────────────────

/** Upper cap for the ratio so the UI never receives Infinity. */
export const BUDGET_RATIO_CAP = 9.99;

/**
 * Budget progress from its aggregate row.
 *
 * Rollover rule (documented here as the source of truth):
 *   carryover = amount − previous_period_spending   (only with rollover)
 *   effective limit = amount + carryover
 * Carryover considers ONLY the immediately preceding period (without
 * recursing backward) and may be negative if the previous period
 * overspent. If the effective limit is ≤ 0, ratio reports as saturated
 * (BUDGET_RATIO_CAP with spending, 1 without) so the UI flags the excess.
 */
export function computeBudgetProgress(row: BudgetSpendRow): BudgetProgress {
  const baseAmount = row.amount ?? 0;
  const rollover = row.rollover === 1;
  const carryover = rollover ? baseAmount - row.spent_previous : 0;
  const budgeted = baseAmount + carryover;
  const spent = row.spent_current;
  const remaining = budgeted - spent;

  let ratio: number;
  if (budgeted > 0) {
    ratio = Math.min(spent / budgeted, BUDGET_RATIO_CAP);
  } else {
    ratio = spent > 0 ? BUDGET_RATIO_CAP : 1;
  }

  return {
    budgetId: row.id,
    categoryId: row.category_id,
    period: row.period === "year" ? "year" : "month",
    rollover,
    baseAmount,
    carryover,
    budgeted,
    spent,
    remaining,
    ratio,
  };
}

// ─── Mutations (local PowerSync writes) ──────────────────────────────────────

export async function createBudgetMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: BudgetDraft,
): Promise<string> {
  if (draft.amount <= 0) {
    throw new Error("The budget amount must be positive");
  }
  const id = generateId();
  const now = nowISO();
  await db.insert(budget).values({
    id,
    user_id: userId,
    category_id: draft.categoryId ?? null,
    amount: draft.amount,
    period: draft.period,
    start_on: draft.startOn ?? null,
    rollover: draft.rollover ? 1 : 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateBudgetMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    category_id: string | null;
    amount: number;
    period: BudgetPeriod;
    start_on: string | null;
    rollover: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, budget, budget.id, id, updates, options);
}

/** PHYSICAL deletion: a budget does not participate in accounting history. */
export async function deleteBudgetMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(budget).where(eq(budget.id, id));
}

// ─── Budget alerts ────────────────────────────────────────────────────────────

/** Alert thresholds (fraction of the effective limit), highest to lowest. */
export const BUDGET_ALERT_THRESHOLDS = [1, 0.8] as const;

export interface BudgetAlert {
  budgetId: string;
  categoryId: string | null;
  /** Crossed threshold: 1 (100 %) or 0.8 (80 %). Only the highest crossed. */
  threshold: number;
  ratio: number;
  spent: number;
  budgeted: number;
  /** User's base currency (spent/budgeted live in it). */
  baseCurrency: string;
  /** ISO start of the evaluated period (stabilizes the dedupe key). */
  periodFromISO: string;
  /** `budgetId|periodFromISO|threshold` — client dedupe key. */
  dedupeKey: string;
}

/**
 * Highest threshold crossed by a progress, or null if none is crossed.
 * Pure so it can be tested without a database.
 */
export function resolveBudgetAlert(
  progress: BudgetProgress,
  periodFromISO: string,
  baseCurrency = "USD",
  thresholds: readonly number[] = BUDGET_ALERT_THRESHOLDS,
): BudgetAlert | null {
  for (const threshold of thresholds) {
    if (progress.ratio >= threshold) {
      return {
        budgetId: progress.budgetId,
        categoryId: progress.categoryId,
        threshold,
        ratio: progress.ratio,
        spent: progress.spent,
        budgeted: progress.budgeted,
        baseCurrency,
        periodFromISO,
        dedupeKey: `${progress.budgetId}|${periodFromISO}|${threshold}`,
      };
    }
  }
  return null;
}

/**
 * Evaluates all user budgets and returns active alerts
 * (80 %/100 % threshold crossed). It does NOT notify or dedupe: that is the
 * platform layer's responsibility (web: `lib/notifications/budget-alerts.ts`),
 * which decides how to show the notification and avoids repeating it the same day.
 */
export async function checkBudgetAlerts(
  db: PowerSyncDrizzleDB,
  userId: string,
  now: Date = new Date(),
  /**
   * CONFIG_OVER_CODE: thresholds (fractions) chosen by the platform. The
   * default is the historical literal, so with no configuration the
   * behavior is identical. Config decides WHEN to warn; nobody touches the
   * spending or budget in `progress`.
   */
  thresholds: readonly number[] = BUDGET_ALERT_THRESHOLDS,
): Promise<BudgetAlert[]> {
  const settingsRows = await db
    .select({ month_start_day: user.month_start_day, base_currency: user.base_currency })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
  const monthStartDay = settingsRows[0]?.month_start_day ?? 1;
  const baseCurrency = settingsRows[0]?.base_currency ?? "USD";
  const ranges = budgetPeriodRanges(now, monthStartDay);
  const rows = await getBudgetSpendQuery(db, userId, ranges);

  const alerts: BudgetAlert[] = [];
  for (const row of rows) {
    const progress = computeBudgetProgress(row);
    const periodFromISO =
      progress.period === "year" ? ranges.year.current.fromISO : ranges.month.current.fromISO;
    const alert = resolveBudgetAlert(progress, periodFromISO, baseCurrency, thresholds);
    if (alert) {
      alerts.push(alert);
    }
  }
  return alerts;
}
